<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { trainingApi } from '../services/trainingApi'
import { trainingStatusColor, trainingStatusLabel } from '../utils'
import CollapsibleText from '@/components/createdUi/CollapsibleText.vue'
import DateRangeFilterCard from '@/components/createdUi/DateRangeFilterCard.vue'
import UniversalFilterTree from '@/components/createdUi/UniversalFilterTree.vue'
import { type IFilterConfig, useTableFilters } from '@/composables/useTableFilters'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'
import type { IFilterStore } from '@/types/IFilterStore'

const props = defineProps<{ kind: 'assignments' | 'programs' | 'campaigns'; mine?: boolean; revision: number }>()
const emit = defineEmits<{ open: [row: any]; create: [] }>()
const { smAndDown } = useDisplay()
const rows = ref<any[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref('')
const selected = ref<string[]>([])
const currentSortString = ref<string | null>(null)
const { itemsPerPage } = useTableItemsPerPage(`training-${props.kind}`, 25)
const defaults = () => ({ search: '', status: [] as string[], employee: [] as string[], department: [] as string[], campaign_filter: [] as string[], category: [] as string[], due_after: null as string | null, due_before: null as string | null, is_overdue: null as boolean | null })
const filters = reactive(defaults())
const columns = computed(() => props.kind === 'programs'
  ? [
      { title: 'Программа', key: 'name' }, { title: 'Код', key: 'code' }, { title: 'Категория', key: 'category', filterKey: 'category' }, { title: 'Версии', key: 'versions', sortable: false },
    ]
  : props.kind === 'campaigns'
    ? [
        { title: 'Кампания', key: 'name' },
        { title: 'Программа', key: 'program_name', sortable: false },
        { title: 'Срок', key: 'due_at', filterKey: 'due' },
        { title: 'Назначено', key: 'assignment_count' },
        { title: 'Завершено', key: 'completed_count' },
        { title: 'Просрочено', key: 'overdue_count' },
        { title: 'Статус', key: 'status', filterKey: 'status' },
      ]
    : [
        ...(!props.mine ? [{ title: 'Сотрудник', key: 'employee_name', filterKey: 'employee' }, { title: 'Подразделение', key: 'department_snapshot.name', filterKey: 'department', sortable: false }] : []),
        { title: 'Программа', key: 'program_name' },
        { title: 'Кампания', key: 'campaign_name', filterKey: 'campaign_filter' },
        { title: 'Срок', key: 'due_at', filterKey: 'due' },
        { title: 'Прогресс', key: 'progress_percent' },
        { title: 'Статус', key: 'status', filterKey: 'status' },
      ])
const assignmentFilters: IFilterConfig[] = [
  { key: 'campaign_filter', labelPrefix: 'Кампания: ', type: 'tree' },
  { key: 'employee', labelPrefix: 'Сотрудник: ', type: 'tree' },
  { key: 'department', labelPrefix: 'Подразделение: ', type: 'tree' },
  { key: 'is_overdue', labelPrefix: 'Просрочено: ', type: 'boolean' },
]
const filterConfigs: IFilterConfig[] = props.kind === 'programs'
  ? [{ key: 'category', labelPrefix: 'Категория: ', type: 'tree' }]
  : [
      { key: 'status', labelPrefix: 'Статус: ', type: 'status', getCustomLabel: value => trainingStatusLabel[value] || value },
      { key: 'due', labelPrefix: 'Срок: ', type: 'date_range', rangeStartKey: 'due_after', rangeEndKey: 'due_before', dateFormat: 'YYYY-MM-DD' },
      ...(props.kind === 'assignments' ? assignmentFilters : []),
    ]
const params = () => {
  // The registry and card share a page. Keep their URL keys separate while
  // retaining the same backend contract for lists, filter choices and exports.
  const { campaign_filter: campaign, ...values } = filters
  return Object.fromEntries(Object.entries({ ...values, campaign, mine: props.mine || undefined, ordering: currentSortString.value }).filter(([, value]) => value !== null && value !== undefined && value !== '' && (!Array.isArray(value) || value.length)).map(([key, value]) => [key, Array.isArray(value) ? value.join(',') : value]))
}
const filterStore = reactive<IFilterStore>({
  filterChoices: {},
  searchChoices: {},
  filterCursors: {},
  filterLoading: {},
  filterStrategies: {},
  async fetchFilterChoices(field, search = '') {
    this.filterLoading[field] = true
    try {
      const data = await trainingApi.filterChoices(props.kind, { ...params(), field: field === 'campaign_filter' ? 'campaign' : field, search })
      const choices = data.results.map((row: any) => ({ id: row.id, label: row.name }))
      if (search) this.searchChoices[field] = choices
      else this.filterChoices[field] = choices
      this.filterCursors[field] = null
      this.filterStrategies[field] = 'flat'
    } catch (e) {
      error.value = 'Не удалось загрузить значения фильтра. Повторите попытку.'
    } finally {
      this.filterLoading[field] = false
    }
  },
})
let requestId = 0
async function fetchRows() {
  const request = ++requestId
  loading.value = true
  error.value = ''
  selected.value = []
  try {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const result = await trainingApi.list(props.kind, { ...params(), page: currentPage.value, page_size: itemsPerPage.value })
    if (request !== requestId) return
    rows.value = result.results
    total.value = result.count
  } catch (e: any) {
    if (request !== requestId) return
    rows.value = []
    total.value = 0
    error.value = e?.response?.data?.detail || 'Не удалось загрузить реестр. Повторите попытку.'
  } finally {
    if (request === requestId) loading.value = false
  }
}
const table = useTableFilters({
  filters,
  filterChoices: computed(() => filterStore.filterChoices) as any,
  searchChoices: computed(() => filterStore.searchChoices) as any,
  filterLoading: filterStore.filterLoading,
  fetchFilterChoices: key => filterStore.fetchFilterChoices(key),
  fetchData: fetchRows,
  currentSortString,
  itemsPerPage,
  resetFilters: () => Object.assign(filters, defaults()),
  nextCursor: ref(null),
  loadedItemsCount: computed(() => rows.value.length),
  totalItemsCount: total,
  sortMap: Object.fromEntries(columns.value.map(column => [column.key, column.key])),
  filterConfigs,
  resetOnUnmount: false,
})
const currentPage = table.currentPage
// PageNumberPagination allows jumping directly to any page. The shared composable
// still owns filter state, chips, query-string persistence and sort indicators.
function updateOptions(options: any) {
  const sort = options.sortBy?.[0]
  const ordering = sort ? `${sort.order === 'desc' ? '-' : ''}${sort.key}` : null
  const changed = ordering !== currentSortString.value || options.itemsPerPage !== itemsPerPage.value
  currentSortString.value = ordering
  itemsPerPage.value = options.itemsPerPage
  table.currentPage.value = changed ? 1 : options.page
  fetchRows()
}
function openFilter(key: string) {
  if (key === 'due') {
    for (const field of ['due_after', 'due_before'] as const) {
      table.tempValues.value[field] = filters[field]?.split('-').reverse().join('.') || ''
    }
  }
  table.handleFilterClick(key)
}
function applyDates() {
  for (const key of ['due_after', 'due_before'] as const) {
    const value = table.tempValues.value[key]
    filters[key] = value ? String(value).split('.').reverse().join('-') : null
  }
  table.filterMenus.value.due = false
  table.triggerServerFilter()
}
async function exportRows(print = false) {
  error.value = ''
  try {
    if (print) await trainingApi.printRegistry(params())
    else await trainingApi.exportRegistry(params())
  } catch {
    error.value = 'Не удалось сформировать документ. Повторите попытку.'
  }
}
watch(() => props.revision, fetchRows)
</script>

<template>
  <div class="training-registry">
    <div class="d-flex flex-wrap align-center gap-2 mb-4">
      <slot name="actions" />
      <VSpacer />
      <template v-if="kind === 'assignments'">
        <VBtn
          variant="outlined"
          color="secondary"
          prepend-icon="ri-file-excel-2-line"
          :disabled="loading || !total"
          @click="exportRows(false)"
        >
          Excel
        </VBtn>
        <VBtn
          variant="outlined"
          color="secondary"
          prepend-icon="ri-printer-line"
          :disabled="loading || !total"
          @click="exportRows(true)"
        >
          Печать
        </VBtn>
      </template>
      <VBtn
        variant="text"
        icon="ri-refresh-line"
        aria-label="Обновить реестр"
        :loading="loading"
        @click="fetchRows"
      />
    </div>
    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      class="mb-3"
    >
      {{ error }} <VBtn variant="text" @click="fetchRows">
        Повторить
      </VBtn>
    </VAlert>
    <VCard>
      <div class="d-flex flex-wrap gap-2 pa-4 align-center">
        <VTextField
          v-model="table.searchFilter.value"
          placeholder="Фильтр по ключевому слову"
          aria-label="Поиск по реестру"
          clearable
          hide-details
          class="training-search"
          @keyup.enter="table.applySearchFilter()"
        />
        <VBtn
          variant="outlined"
          prepend-icon="ri-search-line"
          :disabled="!table.searchFilter.value?.trim()"
          @click="table.applySearchFilter()"
        >
          Найти
        </VBtn>
        <VBtn
          v-if="kind === 'assignments'"
          :variant="filters.is_overdue ? 'tonal' : 'outlined'"
          color="secondary"
          @click="filters.is_overdue = filters.is_overdue ? null : true; table.triggerServerFilter()"
        >
          Просрочено
        </VBtn>
        <VBtn
          v-if="table.hasActiveFilters.value"
          variant="outlined"
          color="secondary"
          prepend-icon="ri-filter-off-line"
          @click="table.renewDataAndResetFilters()"
        >
          Сбросить фильтры
        </VBtn>
      </div>
      <div v-if="table.activeFilterChips.value.length" class="d-flex flex-wrap gap-2 px-4 pb-3">
        <VChip
          v-for="chip in table.activeFilterChips.value"
          :key="`${chip.config?.key}-${chip.value}`"
          closable
          @click:close="table.removeFilter(chip)"
        >
          {{ chip.label }}
        </VChip>
      </div>
      <div v-if="selected.length" class="d-flex flex-wrap align-center gap-2 px-4 pb-3">
        <span>Выбрано: {{ selected.length }}</span>
        <VBtn
          size="small"
          variant="text"
          :disabled="selected.length !== 1"
          @click="emit('open', rows.find(row => row.id === selected[0]))"
        >
          Открыть карточку
        </VBtn>
        <VBtn size="small" variant="text" @click="selected = []">
          Снять выбор
        </VBtn>
      </div>
      <VExpansionPanels v-if="smAndDown" class="px-4 pb-3">
        <VExpansionPanel>
          <VExpansionPanelTitle>Фильтры по столбцам</VExpansionPanelTitle><VExpansionPanelText>
            <div class="d-flex flex-wrap gap-2">
              <template v-for="column in columns.filter(c => c.filterKey)" :key="column.key">
                <VMenu v-model="table.filterMenus.value[column.filterKey!]" :close-on-content-click="false" location="bottom start">
                  <template #activator="{ props: menuProps }">
                    <VBtn
                      v-bind="menuProps"
                      variant="outlined"
                      size="small"
                      :prepend-icon="table.getFilterIcon(column.filterKey!)"
                      :aria-label="`Фильтр: ${column.title}`"
                      @click.stop="openFilter(column.filterKey!)"
                    >
                      {{ column.title }}
                    </VBtn>
                  </template>
                  <DateRangeFilterCard
                    v-if="column.filterKey === 'due'"
                    :date-after="table.tempValues.value.due_after || ''"
                    :date-before="table.tempValues.value.due_before || ''"
                    @update:date-after="table.tempValues.value.due_after = $event"
                    @update:date-before="table.tempValues.value.due_before = $event"
                    @apply="applyDates"
                  />
                  <UniversalFilterTree
                    v-else
                    :field-key="column.filterKey!"
                    :store="filterStore"
                    :model-value="table.tempSelectedFilters.value[column.filterKey!] || []"
                    @update:model-value="table.tempSelectedFilters.value[column.filterKey!] = $event"
                    @apply="table.applyFilter(column.filterKey!)"
                    @cancel="table.filterMenus.value[column.filterKey!] = false"
                  />
                </VMenu>
              </template>
            </div>
          </VExpansionPanelText>
        </VExpansionPanel>
      </VExpansionPanels>
      <VDataTableServer
        v-model="selected"
        :page="table.currentPage.value"
        :items="rows"
        :items-length="total"
        :headers="columns"
        :items-per-page="itemsPerPage"
        :items-per-page-options="[10, 25, 50, 100]"
        :loading="loading"
        :mobile="smAndDown"
        show-select
        item-value="id"
        hover
        class="no-row-gaps cursor-pointer-table custom-data-table custom-data-table-footer"
        @update:options="updateOptions"
        @click:row="(_: unknown, row: any) => emit('open', row.item)"
      >
        <template v-for="column in columns.filter(c => c.filterKey)" :key="column.key" #[`header.${column.key}`]>
          <div class="d-flex align-center">
            <span>{{ column.title }}</span>
            <VIcon
              v-if="column.sortable !== false"
              size="16"
              class="ml-1 custom-sort-icon"
              :icon="table.getSortIcon(column.key)"
              :class="{ 'is-active': table.isSortActive(column.key), 'is-desc': table.isSortDesc(column.key) }"
            />
            <VMenu
              v-if="!smAndDown"
              v-model="table.filterMenus.value[column.filterKey!]"
              :close-on-content-click="false"
              location="bottom start"
            >
              <template #activator="{ props: menuProps }">
                <VBtn
                  v-bind="menuProps"
                  variant="text"
                  size="small"
                  :icon="table.getFilterIcon(column.filterKey!)"
                  :aria-label="`Фильтр: ${column.title}`"
                  @click.stop="openFilter(column.filterKey!)"
                />
              </template>
              <DateRangeFilterCard
                v-if="column.filterKey === 'due'"
                :date-after="table.tempValues.value.due_after || ''"
                :date-before="table.tempValues.value.due_before || ''"
                @update:date-after="table.tempValues.value.due_after = $event"
                @update:date-before="table.tempValues.value.due_before = $event"
                @apply="applyDates"
              />
              <UniversalFilterTree
                v-else
                :field-key="column.filterKey!"
                :store="filterStore"
                :model-value="table.tempSelectedFilters.value[column.filterKey!] || []"
                @update:model-value="table.tempSelectedFilters.value[column.filterKey!] = $event"
                @apply="table.applyFilter(column.filterKey!)"
                @cancel="table.filterMenus.value[column.filterKey!] = false"
              />
            </VMenu>
          </div>
        </template>
        <template #item.name="{ item }">
          <VBtn variant="text" class="training-row-link" @click.stop="emit('open', item)">
            <CollapsibleText :text="item.name" />
          </VBtn>
        </template>
        <template #item.program_name="{ item }">
          <CollapsibleText :text="item.program_name" />
        </template>
        <template #item.campaign_name="{ item }">
          <CollapsibleText :text="item.campaign_name" />
        </template>
        <template #item.due_at="{ item }">
          <span :class="{ 'text-error': item.is_overdue }">{{ new Date(item.due_at).toLocaleDateString('ru-RU') }}</span>
        </template>
        <template #item.progress_percent="{ item }">
          <div class="d-flex align-center gap-2">
            <VProgressLinear :model-value="Number(item.progress_percent)" style="min-width: 60px" /><span>{{ Number(item.progress_percent) }}%</span>
          </div>
        </template>
        <template #item.status="{ item }">
          <VChip :color="trainingStatusColor[item.status]" size="small" variant="tonal">
            {{ trainingStatusLabel[item.status] || item.status }}
          </VChip>
        </template>
        <template #item.versions="{ item }">
          {{ item.versions.length }} · опубликовано: {{ item.versions.filter((v: any) => v.status === 'published').length }}
        </template>
        <template #no-data>
          <div class="pa-6 text-medium-emphasis">
            {{ error ? 'Реестр недоступен' : table.hasActiveFilters.value ? 'По выбранным фильтрам ничего не найдено' : 'Данные отсутствуют' }}
          </div>
        </template>
      </VDataTableServer>
    </VCard>
  </div>
</template>

<style scoped>
.training-search { flex: 1 1 240px; }
.training-row-link { white-space: normal; overflow-wrap: anywhere; text-align: start; height: auto; min-height: 40px; max-width: 100%; padding-block: 8px; }
.training-row-link :deep(.v-btn__content) { white-space: normal; min-width: 0; width: 100%; }
:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px;
  font-weight: 500;
  line-height: 22px;
  padding-inline: 1rem;
}
:deep(.custom-data-table .v-data-table__td) { font-size: 15px; line-height: 20px; padding-inline: 1rem; }
:deep(.v-data-table-footer) { flex-shrink: 0; }
/* Keep every native pagination control visible without a hidden horizontal scroll. */
@media (max-width: 599px) {
  :deep(.v-data-table-footer) { flex-wrap: wrap !important; justify-content: center; row-gap: 8px !important; }
  :deep(.v-data-table-footer__items-per-page) { flex-basis: 100%; justify-content: center; }
}
</style>
